import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FaLock } from "react-icons/fa";
import { FlagIcon } from "@/ui";
import { callWithAuth, getCardCountV2, completeTutorialItem } from "@/api/Api";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";
import { getFeatureUnlockLevel } from "@/features/activation";
import { REVIEW_CARD_COUNT_QUERY_KEY } from "../Review/ReviewInsights";
import { useReviewCooldownActive } from "../Review/reviewCooldown";
import { DICTIONARY_REVIEW_HIGHLIGHT_TIMEOUT_MS } from "./dictionaryReviewHighlight";

// iOS ref: client-native/src/stores/globalStore.ts:1331 REVIEW_UNLOCK_THRESHOLD.
// Review unlocks once the user has saved >= 22 learning words.
const REVIEW_UNLOCK_THRESHOLD = 22;
// iOS sticky-unlock: once review is unlocked it never re-locks. PWA has no MMKV,
// so we persist the sticky bit in localStorage (mirrors checkAndPersistReviewUnlock).
const HAS_UNLOCKED_REVIEW_KEY = "hasUnlockedReview";
// iOS review cooldown: while a post-session cooldown is active the hero shows
// the "finished" state (reviewCount = 0) until it expires. The shared cooldown
// helper (@/components/Review/reviewCooldown) mirrors iOS
// globalStore.reviewCooldownEndTime, and the hero reactively READS it here.
// NOTE: like iOS, nothing currently WRITES the cooldown (startReviewCooldown is
// unused on both platforms), so this branch is dormant today — the machinery is
// kept only for parity with iOS.

type ButtonState = "locked" | "no-words" | "finished" | "reviews-due";

interface DictionaryHeroProps {
  /**
   * When true, shows a pulsing highlight on the review button for a short
   * window (driven by the ?highlightReview=true deep-link from the locked feed
   * CTA / tutorial). Mirrors client-native DictionaryHero highlightReview.
   */
  highlightReview?: boolean;
}

/**
 * DictionaryHero — PWA port of client-native DictionaryHero.tsx.
 * Renders the big known/learning count + a three-variant label line, plus the
 * gated 4-state review button (locked / reviews-due / finished / no-words).
 */
const DictionaryHero: React.FC<DictionaryHeroProps> = ({
  highlightReview = false,
}) => {
  const nav = useNavigate();
  const { knownWordCount, learningWordCount } = useDictionaryStats();

  // Pulsing highlight: latches on when highlightReview flips true, then clears
  // itself after DICTIONARY_REVIEW_HIGHLIGHT_TIMEOUT_MS. Mirrors iOS
  // isReviewHighlightActive + the highlightReview effect.
  const [isReviewHighlightActive, setIsReviewHighlightActive] =
    React.useState(highlightReview);
  React.useEffect(() => {
    if (!highlightReview) {
      setIsReviewHighlightActive(false);
      return undefined;
    }
    setIsReviewHighlightActive(true);
    const timeout = setTimeout(() => {
      setIsReviewHighlightActive(false);
    }, DICTIONARY_REVIEW_HIGHLIGHT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [highlightReview]);

  // Server review card count (word + phrase cards). Shares the query key with
  // ReviewInsights so both stay in sync.
  const { data: cardCountData } = useQuery({
    queryKey: [REVIEW_CARD_COUNT_QUERY_KEY],
    queryFn: async () => callWithAuth(getCardCountV2),
    retry: 5,
    retryDelay: () => 200,
    // iOS refetches getCardCountV2 on focus when >60s have elapsed
    // (CARD_COUNT_REFRESH_INTERVAL_SECONDS = 60). Match that cadence so the
    // hero's "N cards to review" doesn't stay stale for minutes on web.
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });
  const serverReviewCount: number = cardCountData?.count || 0;
  // iOS: during the post-session cooldown the hero shows the finished state.
  // Reactive (re-renders when the cooldown lapses) — mirrors iOS
  // useValue(globalStore.reviewCooldownEndTime).
  const isCooldownActive = useReviewCooldownActive();
  const reviewCount = isCooldownActive ? 0 : serverReviewCount;

  // displayCount: known > 0 ? known : learning > 0 ? learning : 0.
  const displayCount =
    knownWordCount > 0
      ? knownWordCount
      : learningWordCount > 0
      ? learningWordCount
      : 0;

  const totalWords = knownWordCount + learningWordCount;
  const hasWords = totalWords > 0;

  // Sticky unlock: mirrors checkAndPersistReviewUnlock (globalStore.ts:1341-1349).
  // Persist ONLY when already sticky-unlocked OR the learning-word threshold is
  // met — the activation feature gate is intentionally NOT folded into the
  // persisted bit, so review can't latch as permanently unlocked via the journey.
  const stickyUnlocked =
    typeof window !== "undefined" &&
    localStorage.getItem(HAS_UNLOCKED_REVIEW_KEY) === "true";
  const isUnlocked =
    stickyUnlocked || learningWordCount >= REVIEW_UNLOCK_THRESHOLD;
  React.useEffect(() => {
    if (isUnlocked && typeof window !== "undefined") {
      localStorage.setItem(HAS_UNLOCKED_REVIEW_KEY, "true");
    }
  }, [isUnlocked]);

  const buttonState: ButtonState =
    !isUnlocked
      ? "locked"
      : reviewCount > 0
      ? "reviews-due"
      : hasWords
      ? "finished"
      : "no-words";

  const wordText = displayCount === 1 ? "Word" : "Words";
  const wordTextLower = displayCount === 1 ? "word" : "words";

  const boldWord = (text: string) => (
    <span className="font-bold text-[var(--color-grey)]">{text}</span>
  );

  // iOS shows the Spanish FLAG here, not the "🇪🇸" emoji — Windows renders that
  // regional-indicator emoji as the letters "ES", so use the cross-platform SVG
  // FlagIcon (the same one the word cards use) instead.
  const esFlag = (
    <FlagIcon
      countryCode="ES"
      size="small"
      className="inline-block ml-1 align-[-3px]"
    />
  );

  let label: React.ReactNode;
  if (knownWordCount > 0) {
    label = (
      <>
        Spanish {boldWord(wordText)} You Know {esFlag}
      </>
    );
  } else if (learningWordCount > 0) {
    label = (
      <>
        Spanish {boldWord(wordTextLower)} you are learning
      </>
    );
  } else {
    label = (
      <>
        Spanish {boldWord(wordTextLower)} you have clicked
      </>
    );
  }

  const handleReviewPress = () => {
    if (reviewCount > 0) {
      // iOS: pressing the highlighted review button completes the tutorial item.
      if (highlightReview) {
        completeTutorialItem("review_cards").catch((error) => {
          console.error("Error completing tutorial item:", error);
        });
      }
      nav("/review");
    }
  };

  // iOS shows the glow only while highlighting AND there are reviews due.
  const showGlow =
    isReviewHighlightActive && buttonState === "reviews-due";

  return (
    <div className="pt-4 pb-4">
      <div className="flex flex-col items-start justify-center gap-1 pb-4">
        <div className="text-7xl font-bold text-[var(--color-text-primary)] leading-none">
          {displayCount}
        </div>
        <div className="text-lg font-semibold text-[var(--color-grey)]">
          {label}
        </div>
      </div>

      {buttonState === "locked" && (
        <button
          disabled
          className="w-full flex items-center justify-center gap-2 rounded-full text-white font-bold text-lg cursor-not-allowed py-2 px-4 text-center"
          style={{
            backgroundColor: "#3A3A3C",
            minHeight: "58px",
            opacity: 0.5,
          }}
        >
          <FaLock className="w-[18px] h-[18px]" />
          {`Reach Level ${getFeatureUnlockLevel("flashcards") ?? 2} to unlock`}
        </button>
      )}

      {(buttonState === "reviews-due" || buttonState === "finished") && (
        // Subtle pulse on the wrapper while highlighting (scale 1 -> 1.03,
        // 600ms each way, infinite) — mirrors iOS reviewButtonAnimatedStyle.
        <motion.div
          className="relative"
          animate={
            isReviewHighlightActive ? { scale: [1, 1.03, 1] } : { scale: 1 }
          }
          transition={
            isReviewHighlightActive
              ? { duration: 1.2, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.2 }
          }
        >
          {showGlow && (
            // Pulsing blue glow behind the button (opacity 0.4 -> 1, 600ms each
            // way) — mirrors iOS reviewGlow + glowAnimatedStyle.
            <motion.div
              className="absolute rounded-full"
              style={{
                top: -4,
                left: -4,
                right: -4,
                bottom: -4,
                backgroundColor: "var(--color-blue)",
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            />
          )}

          <button
            onClick={handleReviewPress}
            disabled={buttonState !== "reviews-due"}
            className={`relative w-full flex items-center justify-center rounded-full text-white font-bold text-lg py-2 px-4 text-center ${
              buttonState === "reviews-due"
                ? "cursor-pointer"
                : "cursor-not-allowed"
            }`}
            style={{
              backgroundColor:
                buttonState === "finished"
                  ? "var(--color-green)"
                  : "var(--color-blue)",
              minHeight: "58px",
            }}
          >
            {buttonState === "reviews-due"
              ? `${reviewCount} ${reviewCount === 1 ? "card" : "cards"} to review`
              : "Congrats, you're done reviewing! 🎉"}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default DictionaryHero;
